import { env } from '@/config/env';
interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
  body?: any;
  returnType?: 'json' | 'blob';
}

interface LoginResponse {
  token?: string;
  [key: string]: any;
}

interface DocumentParams {
  [key: string]: string | number | boolean;
}

interface UserParams {
  [key: string]: string | number | boolean;
}

interface UploadProgressCallback {
  (percentComplete: number): void;
}

interface TestEmailParams {
  recipientEmail: string;
  subject: string;
  body: string;
}

class ApiClient {
  private subdomain: string | null;
  private baseURL: string;
  private masterApiUrl: string
  private tenantAwareBaseURL: string;
  private token: string | null;

  constructor() {
    // Detect subdomain from current URL
    this.subdomain = this.extractSubdomain(window.location.hostname);

    // Set base URL based on subdomain
    if (this.subdomain === 'master' || this.subdomain === 'admin') {
      this.baseURL = env.masterApiUrl || 'http://localhost:4000/api';
    } else {
      // Tenant-specific API (same backend, resolved via subdomain)
      this.baseURL = env.apiBaseUrl || 'http://localhost:3000/api';
    }

    // Inject subdomain into baseURL for tenant-specific requests
     this.tenantAwareBaseURL = this.injectSubdomainIntoURL(this.baseURL, this.subdomain);

    // Retrieve token from sessionStorage on initialization
    this.token = sessionStorage.getItem('authToken') || null;

    // set masterApiUrl for master specific requests
    this.masterApiUrl = env.masterApiUrl || 'http://localhost:4000/api';
  }

  /**
   * Extract subdomain from hostname
   * Examples:
   * - localhost -> null
   * - smartdocs.ai -> null
   * - tenant.localhost -> 'tenant'
   * - tenant.smartdocs.ai -> 'tenant'
   * - master.smartdocs.ai -> 'master'
   */
  private extractSubdomain(hostname: string): string | null {
    const parts = hostname.split('.');

    // Handle localhost and IP addresses
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null;
    }

    // Check if localhost to test subdomain existence
    const isLocalhost = parts.includes('localhost');

    if ((isLocalhost && parts.length < 2) || (!isLocalhost && parts.length < 3)) {
      return null; // No subdomain
    }

    return parts[0];
  }

  /**
   * Get current subdomain
   */
  getSubdomain(): string | null {
    return this.subdomain;
  }

  /**
   * Inject subdomain into baseURL for tenant-specific requests
   * Examples:
   * - http://localhost:3000/api + 'demo' -> http://demo.localhost:3000/api
   * - https://api.yourdomain.com/api + 'acme' -> https://acme.yourdomain.com/api
   * - http://www.example.com/api + 'tenant1' -> http://tenant1.example.com/api
   */
  private injectSubdomainIntoURL(baseURL: string, subdomain: string | null): string {
    // If no subdomain, return baseURL as-is
    if (!subdomain) {
      return baseURL;
    }

    try {
      const url = new URL(baseURL);
      const hostname = url.hostname;

      // Handle localhost specially
      if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        // localhost -> subdomain.localhost
        // 127.0.0.1 -> subdomain.localhost (convert IP to localhost for subdomain support)
        url.hostname = `${subdomain}.localhost`;
        return url.toString();
      }

      // Handle www and other prefixes
      const parts = hostname.split('.');

      // Check if hostname already has the subdomain
      if (parts[0] === subdomain) {
        return baseURL; // Already has the correct subdomain
      }

      // Remove 'www' if present and inject subdomain
      if (parts[0] === 'www') {
        parts[0] = subdomain;
      } else if (parts.length >= 2) {
        // Inject subdomain at the beginning
        parts.unshift(subdomain);
      } else {
        // Single domain (unlikely in production), just prepend
        parts.unshift(subdomain);
      }

      url.hostname = parts.join('.');
      return url.toString();
    } catch (error) {
      console.error('Error injecting subdomain into URL:', error);
      return baseURL; // Fallback to original URL
    }
  }

  // Set authentication token and store it in sessionStorage
  setToken(token: string): void {
    this.token = token;
    sessionStorage.setItem('authToken', token);
  }

  // Clear authentication token from memory and sessionStorage
  clearToken(): void {
    this.token = null;
    sessionStorage.removeItem('authToken');
  }

  // Get the current authentication token
  getToken(): string | null {
    return this.token;
  }

  // Generic request handler
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {

    // Check if request is master specific
    const isMasterRequest = endpoint.slice(1).split('/')[0] === 'master';

    const url = isMasterRequest ? `${this.masterApiUrl}${endpoint}` : `${this.tenantAwareBaseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Handle FormData (for file uploads)
    if (options.body instanceof FormData) {
      delete headers['Content-Type']; // Let browser set it with boundary
    }

    const config: RequestInit = {
      ...options,
      headers,
      body: options.body instanceof FormData
        ? options.body
        : options.body ? JSON.stringify(options.body) : undefined,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized (token expired)
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      // If response is for file download, return blob directly
      if (options.returnType === 'blob') {
        if (!response.ok) {
          // Attempt to parse JSON error if available, otherwise use status text
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        return response.blob() as Promise<T>;
      }

      // Parse JSON response
      const data = await response.json();

      // Handle non-OK responses
      if (!response.ok) {
        // Check for error message in 'message', 'error', or fallback to HTTP status
        throw new Error(data.message || data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error [${options.method || 'GET'} ${endpoint}]:`, error);
      throw error;
    }
  }

  // Convenience methods
  get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T = any>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  put<T = any>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  patch<T = any>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  delete<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE', body: options.body });
  }

  // === AUTHENTICATION ENDPOINTS ===

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.post<LoginResponse>('/auth/login', { email, password });
    if (response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async logout(): Promise<any> {
    const response = await this.post('/auth/logout');
    this.clearToken();
    return response;
  }

  async verifyToken(): Promise<any> {
    return this.get('/auth/verify');
  }

  // New: Request password reset link
  async forgotPassword(email: string): Promise<any> {
    return this.post('/auth/forgot-password', { email });
  }

  // New: Reset password with token
  async resetPassword(token: string, newPassword: string): Promise<any> {
    return this.post(`/auth/reset-password/${token}`, { newPassword });
  }

  // === DOCUMENTS ENDPOINTS ===

  async getDocuments(params: DocumentParams = {}): Promise<any> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.get(`/documents?${query}`);
  }

  async getDocument(id: string | number): Promise<any> {
    return this.get(`/documents/${id}`);
  }

  async uploadDocument(file: File, onProgress?: UploadProgressCallback): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', encodeURIComponent(file.name));

    // For progress tracking, use XMLHttpRequest
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', `${this.tenantAwareBaseURL}/documents/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      xhr.send(formData);
    });
  }

  async deleteDocument(id: string | number, options: any = {}): Promise<any> {
    return this.delete(`/documents/${id}`, { body: options });
  }

  async reprocessDocument(id: string | number): Promise<any> {
    return this.post(`/documents/${id}/reprocess`);
  }

  // New method to download a document
  async downloadDocument(id: string | number): Promise<Blob> {
    // The endpoint should be `/documents/:id/download` as per common practice,
    // and the request needs to handle a binary response (blob).
    // We pass a custom option to the generic request handler to indicate
    // that it should return a blob instead of parsing JSON.
    return this.get<Blob>(`/documents/${id}/download`, { returnType: 'blob' });
  }

  // === ANALYTICS ENDPOINTS ===

  async getAnalyticsOverview(period: string = '30d'): Promise<any> {
    return this.get(`/analytics/overview?period=${period}`);
  }

  async getQuotaStatus(): Promise<any> {
    return this.get(`/analytics/quota-status`);
  }

  async getQueriesOverTime(period: string = '30d', interval: string = 'day'): Promise<any> {
    return this.get(`/analytics/queries-over-time?period=${period}&interval=${interval}`);
  }

  async getTopDocuments(period: string = '30d', limit: number = 10): Promise<any> {
    return this.get(`/analytics/top-documents?period=${period}&limit=${limit}`);
  }

  async getCommonQueries(period: string = '30d', limit: number = 20): Promise<any> {
    return this.get(`/analytics/common-queries?period=${period}&limit=${limit}`);
  }

  async getUserActivity(period: string = '30d'): Promise<any> {
    return this.get(`/analytics/user-activity?period=${period}`);
  }

  async getUserAnalytics(id: string | number, period: string = '30d'): Promise<any> {
    return this.get(`/analytics/user-analytics/${id}?period=${period}`);
  }

  async getPerformance(period: string = '30d'): Promise<any> {
    return this.get(`/analytics/performance?period=${period}`);
  }

  async exportAnalytics(period: string, metrics: string[], format: string = 'csv'): Promise<Blob> {
    const response = await fetch(`${this.tenantAwareBaseURL}/analytics/export`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ period, metrics, format }),
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // === USERS ENDPOINTS ===

  async getUsers(params: UserParams = {}): Promise<any> {
    const query = new URLSearchParams(params as Record<string, string>).toString();

    return this.get(`/users?${query}`);
  }

  async getUser(id: string | number): Promise<any> {
    return this.get(`/users/${id}`);
  }

  async createUser(userData: any): Promise<any> {
    return this.post('/users', userData);
  }

  async updateUser(id: string | number, updates: any): Promise<any> {
    return this.put(`/users/${id}`, updates);
  }

  async deleteUser(id: string | number): Promise<any> {
    return this.delete(`/users/${id}`);
  }

  async resetUserPassword(id: string | number): Promise<any> {
    return this.post(`/users/${id}/reset-password`);
  }

  // === SETTINGS ENDPOINTS ===

  async getSettings(): Promise<any> {
    return this.get('/settings');
  }

  async updateSettings(category: string, settings: any): Promise<any> {
    return this.put('/settings', { [category]: settings });
  }

  async testEmailSettings({ recipientEmail, subject, body }: TestEmailParams): Promise<any> {
    return this.post('/settings/test-email', { recipientEmail, subject, body });
  }

  // === SYSTEM ENDPOINTS ===

  async getSystemHealth(): Promise<any> {
    return this.get('/system/health');
  }

  async runHealthCheck(): Promise<any> {
    return this.post('/system/health-check');
  }

  async backupDatabase(): Promise<any> {
    return this.post('/system/backup');
  }

  async clearCache(): Promise<any> {
    return this.post('/system/clear-cache');
  }

  async reindexDocuments(): Promise<any> {
    return this.post('/system/reindex');
  }

  // === TENANTS ENDPOINTS ===

  async getTenantBranding(): Promise<any> {
    this.get('/tenant/branding');
  }

  // === CHAT ENDPOINTS ===

  async sendMessage(text: string): Promise<any> {
    // Corrected to pass the body directly to this.post and use the correct endpoint
    return this.post('/chat', { message: text });
  }

  // === LICENSE ENDPOINTS (Standalone Mode) ===

  // /**
  //  * Generate a new license key
  //  */
  // async generateLicense(data: any): Promise<any> {
  //   return this.post('/master/license/generate', data);
  // }
  //
  // /**
  //  * Get all generated licenses
  //  */
  // async getLicenses(filters?: {
  //   page?: number;
  //   limit?: number;
  //   isActive?: boolean;
  //   search?: string;
  // }): Promise<any> {
  //   const params = new URLSearchParams();
  //   if (filters?.page) params.append('page', String(filters.page));
  //   if (filters?.limit) params.append('limit', String(filters.limit));
  //   if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
  //   if (filters?.search) params.append('search', filters.search);
  //
  //   const queryString = params.toString();
  //   return this.get(`/master/license/list${queryString ? `?${queryString}` : ''}`);
  // }
  //
  // /**
  //  * Validate a license key
  //  */
  // async validateLicense(licenseKey: string): Promise<any> {
  //   return this.post('/master/license/validate-key', { licenseKey });
  // }
  //
  // /**
  //  * Revoke/deactivate a license
  //  */
  // async revokeLicense(licenseKey: string): Promise<any> {
  //   return this.post('/master/license/revoke', { licenseKey });
  // }
  //
  /**
   * Activate a license with license key
   */
  async activateLicense(licenseKey: string): Promise<any> {
    return this.post('/license/activate', { licenseKey });
  }

  /**
   * Get current license status (for activated license)
   */
  async getLicenseStatus(): Promise<any> {
    return this.get('/license/status');
  }

  // /**
  //  * Renew a license
  //  */
  // async renewLicense(licenseKey: string, newExpirationDate: string, transactionId?: string): Promise<any> {
  //   return this.post('/master/license/renew', { licenseKey, newExpirationDate, transactionId });
  // }
  //
  // /**
  //  * Get quota usage
  //  */
  // async getLicenseQuotas(): Promise<any> {
  //   return this.get('/master/license/quotas');
  // }

  // === OFFLINE LICENSE ENDPOINTS ===

  /**
   * Activate offline license from uploaded file
   */
  async activateOfflineLicense(licenseFile: any): Promise<any> {
    return this.post('/license/offline-activate', licenseFile);
  }

  /**
   * Get offline license status
   */
  async getOfflineLicenseStatus(): Promise<any> {
    return this.get('/license/offline-status');
  }

  /**
   * Validate offline license file without activating
   */
  async validateOfflineLicenseFile(licenseFile: any): Promise<any> {
    return this.post('/license/offline-validate', licenseFile);
  }

  // === SYNC ENDPOINTS ===
  /**
   * trigger Config & Usage sync
   */
  async triggerConfigAndUsageSync(): Promise<any> {
    return this.post('/sync/trigger');
  }
}

// Create singleton instance
const api = new ApiClient();

export default api;
